import { t } from 'i18next';
import uniqueId from '../../utils/uniqueId';
import { forwardRef, useContext, useEffect, useState, useMemo } from 'react';
import DatePicker from 'react-date-picker';

import { ShipyardContext, UserStateContext, FormContext } from '../Context';

import './LabeledDatePicker.scss';

const LabeledDatePicker = forwardRef((props, ref) => {
    let { changeHandler, blurHandler } = props;

    const { userSettings } = useContext(ShipyardContext);
    const { doingAjax } = useContext(UserStateContext);
    const { triggerValidation, validationFinished } = useContext(FormContext);
    const [uid] = useState(uniqueId());
    const [isDisabled, setIsDisabled] = useState(
        typeof doingAjax === 'boolean' ? doingAjax : typeof props?.disabled === 'boolean' ? props.disabled : false
    );
    const [value, setValue] = useState(typeof props?.value === 'string' ? props.value : '');
    const [labelText, setLabelText] = useState(typeof props?.label === 'string' ? props.label : '');
    const [minDate, setMinDate] = useState(props?.minDate ? new Date(props.minDate) : null);
    const [maxDate, setMaxDate] = useState(props?.maxDate ? new Date(props.maxDate) : null);
    const [isValid, setValid] = useState(true);
    const [validated, setValidated] = useState(typeof props?.validated === 'boolean' ? props.validated : false);
    const [formKey] = useState(!!props.formKey ? props.formKey : `date-picker-${uid}`);
    const [isRequired, setIsRequired] = useState(typeof props?.required === 'boolean' ? props.required : false);

    const handleDateChange = useMemo(() => {
        return (event) => {
            if (typeof changeHandler === 'function') changeHandler(event);
            setValue(!!event ? event : '');

            if (!!event) {
                setValid(true);
            }
        };
    }, [changeHandler]);

    const handleDateBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            if (typeof blurHandler === 'function') blurHandler(event);

            if (value === '' && isRequired) {
                setValid(false);
            } else {
                setValid(true);
            }

            setValidated(true);
        }
    };

    const datePickerJSX = useMemo(() => {
        return (
            <DatePicker
                locale={userSettings?.LanguagePreference ? userSettings.LanguagePreference : 'en-US'}
                onChange={handleDateChange}
                disabled={doingAjax ? doingAjax : isDisabled ? isDisabled : false}
                value={value}
                minDate={minDate}
                maxDate={maxDate}
                dayPlaceholder="dd"
                monthPlaceholder="mm"
                yearPlaceholder="yyyy"
                nativeInputAriaLabel={labelText}
                openCalendarOnFocus={false}
            />
        );
    }, [userSettings.LanguagePreference, doingAjax, isDisabled, value, minDate, maxDate, labelText, handleDateChange]);

    useEffect(() => {
        if (typeof props?.label === 'string') setLabelText(props.label);
    }, [props.label]);

    useEffect(() => {
        setMinDate(props?.minDate ? new Date(props.minDate) : null);
    }, [props.minDate]);

    useEffect(() => {
        setMaxDate(props?.maxDate ? new Date(props.maxDate) : null);
    }, [props.maxDate]);

    useEffect(() => {
        setValidated(typeof props?.validated === 'boolean' ? props.validated : false);
        setValid((prev) => (!prev ? !prev : prev));
    }, [props.validated]);

    useEffect(() => {
        setIsDisabled((prev) =>
            typeof props?.disabled === 'boolean' ? props.disabled : typeof doingAjax === 'boolean' ? doingAjax : prev
        );
    }, [props.disabled, doingAjax]);

    useEffect(() => {
        if (triggerValidation && isRequired) {
            setValidated(true);

            if (value === '') {
                setValid(false);
                validationFinished(true);
                return;
            }

            setValid(true);
            validationFinished(true);
        }
    }, [value, triggerValidation, validationFinished, isValid, isRequired]);

    useEffect(() => {
        setValue((prev) => (props?.value !== null ? props.value : prev));
    }, [props.value]);

    useEffect(() => {
        setIsRequired((prev) => (typeof props?.required === 'boolean' ? props.required : prev));
    }, [props.required]);

    const invalidJSX = useMemo(() => {
        return !isValid && validated ? (
            <div
                className="invalid-reason"
                id={'input_err-' + uid}
            >
                {t('DatePicker.InvalidMessage')}
            </div>
        ) : null;
    }, [isValid, validated, uid]);

    return (
        <div
            className={Array.isArray(props?.classes) ? ['date-picker-container', ...props.classes].join(' ') : 'date-picker-container'}
            onBlur={typeof handleDateBlur === 'function' ? handleDateBlur : null}
        >
            <label id={'date-picker-' + uid}>
                {isRequired && !!labelText ? <span className="is-required">&#8270;</span> : null} {!!labelText ? labelText : null}
                {!!labelText ? <br /> : null}
                {datePickerJSX}
            </label>
            {invalidJSX}
            <input
                className="hidden"
                type="hidden"
                value={value}
                ref={ref}
                data-form-key={formKey}
                disabled
                aria-invalid={!isValid}
            />
        </div>
    );
});

export default LabeledDatePicker;
